import { Fragment, memo, useEffect, useMemo, useState } from 'react';

import { ChevronDownIcon, FileBoxIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, DropdownMenuLabel, DropdownMenuSeparator } from '@air/primitive-dropdown-menu';
import { cn } from '@air/utils-tailwind';

import { type AiModelConfig, useAiModels } from '../../../api/aimodels/useAiModels';
import { useWorkspaceId } from '../../../hooks/useWorkspaceId';
import { ModelItem } from '../ModelPicker/ModelItem';

type TransformModelTypeFilter = 'image' | 'video' | 'imageAndVideo';

const matchesTypeFilter = (model: AiModelConfig, filter: TransformModelTypeFilter): boolean => {
  if (filter === 'imageAndVideo') {
    return model.type === 'image' || model.type === 'video';
  }

  return model.type === filter;
};

export type TransformModelSelectProps = {
  /** Filter models by type. `'imageAndVideo'` includes both. */
  typeFilter: TransformModelTypeFilter;
  /**
   * Match against `model.models.edit`. When found, that entry is used as the default selection on
   * first render. If not found in the filtered list, the first filtered entry is used as the
   * default.
   */
  recommendedEditModelId?: string;
  value: AiModelConfig | null;
  onChange: (model: AiModelConfig) => void;
  disabled?: boolean;
};

export const TransformModelSelect = memo(
  ({ disabled, onChange, recommendedEditModelId, typeFilter, value }: TransformModelSelectProps) => {
    const [open, setOpen] = useState(false);
    const { workspaceId } = useWorkspaceId();
    const { data: allModels, isLoading } = useAiModels({ workspaceId });

    const models = useMemo(
      () => (allModels ?? []).filter((m) => matchesTypeFilter(m, typeFilter)),
      [allModels, typeFilter],
    );
    const types = useMemo(() => [...new Set(models.map((m) => m.type))], [models]);

    // Pick a default once models are loaded and the caller hasn't already set one. Prefer the
    // recommended entry; fall back to the first filtered model.
    useEffect(() => {
      if (value !== null || models.length === 0) {
        return;
      }
      const recommended = recommendedEditModelId
        ? models.find((m) => m.models.edit === recommendedEditModelId)
        : undefined;
      onChange(recommended ?? models[0]);
    }, [value, models, recommendedEditModelId, onChange]);

    if (isLoading) {
      return (
        <Button
          appearance="outline"
          color="gray"
          disabled
          prefix={<FileBoxIcon className="shrink-0" size="xs" />}
          size="small"
        >
          <span className="truncate">Loading…</span>
        </Button>
      );
    }

    if (models.length === 0) {
      return null;
    }

    return (
      <DropdownMenu
        align="start"
        className="w-64"
        onOpenChange={setOpen}
        open={open}
        side="bottom"
        sideOffset={8}
        trigger={
          <Button
            appearance="outline"
            color="gray"
            disabled={disabled}
            prefix={<FileBoxIcon className="shrink-0" size="xs" />}
            size="small"
            suffix={
              <ChevronDownIcon
                className={cn('shrink-0 ads:text-icon-subtle text-gray-9 transition-transform', open && 'rotate-180')}
                size="xs"
              />
            }
          >
            <span className="truncate">{value?.name ?? 'Select model'}</span>
          </Button>
        }
      >
        {types.map((type, index) => {
          const group = models.filter((m) => m.type === type);

          return (
            <Fragment key={type}>
              {index > 0 && <DropdownMenuSeparator />}
              {types.length > 1 && <DropdownMenuLabel className="px-2 pb-2">{type}</DropdownMenuLabel>}
              {group.map((model) => (
                <ModelItem isSelected={value?.name === model.name} key={model.name} model={model} onSelect={onChange} />
              ))}
            </Fragment>
          );
        })}
      </DropdownMenu>
    );
  },
);

TransformModelSelect.displayName = 'TransformModelSelect';
